import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { API } from '../../utils/api';
import MessageContent from '../shared/MessageContent';

const WS_BASE = API.replace('https://', 'wss://').replace('http://', 'ws://').replace('/api', '');

const CompactChatCollabModule = ({ showToast }) => {
  const { token, user } = useAuth();
  const goToProfile = (userId) => { if (userId) window.location.href = `/profile/${userId}`; };
  const [activeTab, setActiveTab] = useState('chat');
  const [collapsed, setCollapsed] = useState(false);

  // Chat state
  const [rooms, setRooms] = useState([]);
  const [activeRoom, setActiveRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [onlineCount, setOnlineCount] = useState(0);
  const [chatLoading, setChatLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);

  // Collaboration state
  const [collabRooms, setCollabRooms] = useState([]);
  const [activeCollab, setActiveCollab] = useState(null);
  const [collabDetail, setCollabDetail] = useState(null);
  const [collabMsg, setCollabMsg] = useState('');
  const [pinUrl, setPinUrl] = useState('');

  const xhr = useCallback((method, url, body) => {
    return new Promise((resolve, reject) => {
      const x = new XMLHttpRequest();
      x.open(method, `${API}${url}`, true);
      x.setRequestHeader('Content-Type', 'application/json');
      if (token) x.setRequestHeader('Authorization', `Bearer ${token}`);
      x.timeout = 15000;
      x.onload = () => {
        try { resolve(JSON.parse(x.responseText)); }
        catch { resolve({}); }
      };
      x.onerror = () => reject('Network error');
      x.ontimeout = () => reject('Timeout');
      x.send(body ? JSON.stringify(body) : null);
    });
  }, [token]);

  // Load chat rooms
  useEffect(() => {
    if (!token) return;
    xhr('GET', '/chat/rooms').then(d => setRooms(d.rooms || [])).catch(() => {});
    xhr('GET', '/chat/online').then(d => setOnlineCount(d.online_count || 0)).catch(() => {});
    xhr('GET', '/collab-rooms/rooms').then(d => setCollabRooms(d.rooms || d || [])).catch(() => {});
  }, [token, xhr]);

  // WebSocket for chat
  useEffect(() => {
    if (!token || !activeRoom) return;
    const ws = new WebSocket(`${WS_BASE}/api/ws/chat/${activeRoom}?token=${token}`);
    ws.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data);
        if (msg.type === 'message') setMessages(prev => [...prev, msg]);
      } catch {}
    };
    wsRef.current = ws;
    return () => ws.close();
  }, [token, activeRoom]);

  const loadMessages = async (roomId) => {
    setActiveRoom(roomId);
    setChatLoading(true);
    try {
      const d = await xhr('GET', `/chat/rooms/${roomId}/messages?limit=50`);
      setMessages(d.messages || []);
    } catch {}
    setChatLoading(false);
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !activeRoom) return;
    try {
      await xhr('POST', `/chat/rooms/${activeRoom}/messages`, { content: newMessage });
      setNewMessage('');
      // Reload messages to include the new one
      const d = await xhr('GET', `/chat/rooms/${activeRoom}/messages?limit=50`);
      setMessages(d.messages || []);
    } catch { showToast?.('Send failed', 'error'); }
  };

  const loadCollabRoom = async (roomId) => {
    setActiveCollab(roomId);
    try {
      const d = await xhr('GET', `/collaboration/rooms/${roomId}`);
      setCollabDetail(d);
    } catch {}
  };

  const sendCollabMsg = async () => {
    if (!collabMsg.trim() || !activeCollab) return;
    try {
      await xhr('POST', `/collaboration/rooms/${activeCollab}/messages`, { content: collabMsg });
      setCollabMsg('');
      loadCollabRoom(activeCollab);
    } catch {}
  };

  const pinUrlToCollab = async () => {
    if (!pinUrl.trim() || !activeCollab) return;
    try {
      await xhr('POST', `/collaboration/rooms/${activeCollab}/pins`, { url: pinUrl, title: pinUrl });
      setPinUrl('');
      loadCollabRoom(activeCollab);
      showToast?.('URL pinned', 'success');
    } catch {}
  };

  const scrollToBottom = () => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  useEffect(scrollToBottom, [messages]);

  if (collapsed) {
    return (
      <div
        data-testid="chat-collab-module-collapsed"
        onClick={() => setCollapsed(false)}
        style={{
          background: 'rgba(252, 211, 77,0.08)', border: '1px solid rgba(252, 211, 77,0.2)',
          borderRadius: 12, padding: '10px 16px', cursor: 'pointer', display: 'flex',
          alignItems: 'center', justifyContent: 'space-between', marginBottom: 12
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <i className="fas fa-comments" style={{ color: '#047857' }} />
          <span style={{ color: '#334155', fontSize: '0.85rem', fontWeight: 600 }}>Chat & Collaborate</span>
          <span style={{ color: '#22c55e', fontSize: '0.65rem' }}>{onlineCount} online</span>
        </div>
        <i className="fas fa-chevron-down" style={{ color: '#71717a', fontSize: '0.7rem' }} />
      </div>
    );
  }

  const tabStyle = (t) => ({
    flex: 1, padding: '6px 0', textAlign: 'center', cursor: 'pointer', fontSize: '0.78rem',
    fontWeight: 700, borderBottom: `2px solid ${activeTab === t ? '#10B981' : 'transparent'}`,
    color: activeTab === t ? '#047857' : '#64748B', transition: 'all 0.2s'
  });

  return (
    <div data-testid="chat-collab-module" style={{
      background: 'rgba(255, 255, 255, 0.95)', border: '1px solid rgba(16, 185, 129, 0.25)',
      borderRadius: 12, overflow: 'hidden', marginBottom: 12,
      boxShadow: '0 4px 16px rgba(14, 165, 233, 0.08)'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '8px 12px', borderBottom: '1px solid rgba(16, 185, 129, 0.18)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <i className="fas fa-comments" style={{ color: '#047857', fontSize: '0.85rem' }} />
          <span style={{ color: '#0F172A', fontSize: '0.82rem', fontWeight: 700 }}>Chat & Collaborate</span>
          <span style={{ background: '#22c55e', width: 6, height: 6, borderRadius: '50%', display: 'inline-block' }} />
          <span style={{ color: '#22c55e', fontSize: '0.65rem' }}>{onlineCount}</span>
        </div>
        <button onClick={() => setCollapsed(true)} style={{
          background: 'none', border: 'none', color: '#64748B', cursor: 'pointer', fontSize: '0.8rem'
        }} data-testid="chat-collab-collapse-btn">
          <i className="fas fa-chevron-up" />
        </button>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid rgba(16, 185, 129, 0.18)' }}>
        <div style={tabStyle('chat')} onClick={() => setActiveTab('chat')} data-testid="chat-tab-btn">
          <i className="fas fa-comment-dots" style={{ marginRight: 4 }} /> Chat
        </div>
        <div style={tabStyle('collab')} onClick={() => setActiveTab('collab')} data-testid="collab-tab-btn">
          <i className="fas fa-users" style={{ marginRight: 4 }} /> Collaborate
        </div>
      </div>

      {/* CHAT TAB */}
      {activeTab === 'chat' && (
        <div style={{ maxHeight: 320, display: 'flex', flexDirection: 'column' }}>
          {!activeRoom ? (
            <div style={{ padding: 8, overflowY: 'auto', maxHeight: 280 }}>
              {rooms.length === 0 && (
                <p style={{ color: '#71717a', fontSize: '0.75rem', textAlign: 'center', padding: 12 }}>
                  No chat rooms yet. Visit the Chat page to create one!
                </p>
              )}
              {rooms.map(r => (
                <div key={r.id || r._id} onClick={() => loadMessages(r.id || r._id)}
                  data-testid={`chat-room-${r.id || r._id}`}
                  style={{
                    padding: '10px 12px', borderRadius: 8, cursor: 'pointer', marginBottom: 4,
                    background: 'rgba(16, 185, 129, 0.06)', border: '1px solid rgba(16, 185, 129, 0.18)',
                    transition: 'background 0.15s', touchAction: 'manipulation',
                    minHeight: 44, WebkitTapHighlightColor: 'rgba(16, 185, 129, 0.2)'
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(16, 185, 129, 0.14)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'rgba(16, 185, 129, 0.06)'}
                >
                  <div style={{ color: '#0F172A', fontSize: '0.8rem', fontWeight: 700 }}>{r.name}</div>
                  <div style={{ color: '#64748B', fontSize: '0.65rem' }}>
                    {r.member_count || 0} members {r.unread_count > 0 && <span style={{ color: '#047857', fontWeight: 700 }}>({r.unread_count} new)</span>}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', height: 280 }}>
              <div style={{
                padding: '6px 10px', display: 'flex', alignItems: 'center', gap: 8,
                borderBottom: '1px solid rgba(16, 185, 129, 0.18)'
              }}>
                <button onClick={() => { setActiveRoom(null); setMessages([]); }}
                  style={{ background: 'none', border: 'none', color: '#047857', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 700 }}
                  data-testid="chat-back-btn">
                  <i className="fas fa-arrow-left" /> Back
                </button>
                <span style={{ color: '#0F172A', fontSize: '0.75rem', fontWeight: 700 }}>
                  {rooms.find(r => (r.id || r._id) === activeRoom)?.name}
                </span>
              </div>
              <div style={{ flex: 1, overflowY: 'auto', padding: '6px 8px' }}>
                {chatLoading && <p style={{ color: '#64748B', fontSize: '0.7rem', textAlign: 'center' }}>Loading...</p>}
                {messages.map((m, i) => {
                  const isOwn = m.is_own ?? (m.user_id === user?.id);
                  return (
                  <div key={m.id || i} style={{
                    marginBottom: 4, padding: '6px 10px', borderRadius: 8,
                    background: isOwn ? 'rgba(16, 185, 129, 0.12)' : 'rgba(14, 165, 233, 0.08)',
                    border: isOwn ? '1px solid rgba(16, 185, 129, 0.25)' : '1px solid rgba(14, 165, 233, 0.15)',
                    display: 'flex', gap: 6, alignItems: 'flex-start'
                  }}>
                    <div
                      onClick={() => m.user_id && goToProfile(m.user_id)}
                      style={{ cursor: 'pointer', flexShrink: 0, marginTop: 2 }}
                      data-testid={`chat-msg-avatar-${i}`}
                    >
                      {m.avatar_url ? (
                        <img src={m.avatar_url} alt="" style={{ width: 22, height: 22, borderRadius: '50%', objectFit: 'cover', border: '1px solid rgba(16, 185, 129, 0.35)' }} />
                      ) : (
                        <div style={{ width: 22, height: 22, borderRadius: '50%', background: 'rgba(16, 185, 129, 0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.55rem', color: '#047857', fontWeight: 700 }}>
                          {(m.username || 'U')[0].toUpperCase()}
                        </div>
                      )}
                    </div>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <span
                        onClick={() => m.user_id && goToProfile(m.user_id)}
                        style={{ color: '#047857', fontSize: '0.65rem', fontWeight: 700, cursor: 'pointer' }}
                        data-testid={`chat-msg-username-${i}`}
                      >{m.username || 'User'}</span>
                      <MessageContent text={m.content} style={{ color: '#0F172A', fontSize: '0.8rem', margin: '2px 0 0', lineHeight: 1.4 }} />
                    </div>
                  </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>
              <div style={{ display: 'flex', gap: 6, padding: '8px', borderTop: '1px solid rgba(16, 185, 129, 0.18)' }}>
                <textarea value={newMessage} onChange={e => setNewMessage(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      sendMessage();
                    }
                  }}
                  placeholder="Message... (Enter to send, Shift+Enter for new line)"
                  data-testid="chat-message-input"
                  rows={3}
                  style={{
                    flex: 1, background: '#ffffff', border: '1px solid rgba(14, 165, 233, 0.28)',
                    borderRadius: 8, padding: '8px 10px', color: '#0F172A', fontSize: '0.8rem', outline: 'none',
                    resize: 'none', fontFamily: 'inherit', minHeight: 40
                  }}
                />
                <button onClick={sendMessage} data-testid="chat-send-btn" style={{
                  background: 'linear-gradient(135deg, #10B981, #059669)', border: 'none', borderRadius: 8, padding: '6px 14px',
                  color: '#ffffff', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 700,
                  boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)'
                }}>Send</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* COLLABORATION TAB */}
      {activeTab === 'collab' && (
        <div style={{ maxHeight: 320, display: 'flex', flexDirection: 'column' }}>
          {!activeCollab ? (
            <div style={{ padding: 8, overflowY: 'auto', maxHeight: 280 }}>
              {collabRooms.length === 0 && (
                <p style={{ color: '#71717a', fontSize: '0.75rem', textAlign: 'center', padding: 12 }}>
                  No collaboration rooms. Visit Collaboration to create one!
                </p>
              )}
              {(Array.isArray(collabRooms) ? collabRooms : []).map(r => (
                <div key={r.room_id || r.id || r._id} onClick={() => loadCollabRoom(r.room_id || r.id || r._id)}
                  data-testid={`collab-room-${r.room_id || r.id || r._id}`}
                  style={{
                    padding: '10px 12px', borderRadius: 8, cursor: 'pointer', marginBottom: 4,
                    background: 'rgba(245, 158, 11,0.06)', border: '1px solid rgba(245, 158, 11,0.1)',
                    transition: 'background 0.15s', touchAction: 'manipulation',
                    minHeight: 44, WebkitTapHighlightColor: 'rgba(245, 158, 11,0.2)'
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(245, 158, 11,0.15)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'rgba(245, 158, 11,0.06)'}
                >
                  <div style={{ color: '#334155', fontSize: '0.78rem', fontWeight: 600 }}>{r.name}</div>
                  <div style={{ color: '#71717a', fontSize: '0.65rem' }}>
                    {r.member_count || r.members?.length || 0} members | {r.pin_count || r.pins?.length || 0} pins
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', height: 280 }}>
              <div style={{
                padding: '6px 10px', display: 'flex', alignItems: 'center', gap: 8,
                borderBottom: '1px solid rgba(255,255,255,0.06)'
              }}>
                <button onClick={() => { setActiveCollab(null); setCollabDetail(null); }}
                  style={{ background: 'none', border: 'none', color: '#60a5fa', cursor: 'pointer', fontSize: '0.75rem' }}
                  data-testid="collab-back-btn">
                  <i className="fas fa-arrow-left" /> Back
                </button>
                <span style={{ color: '#334155', fontSize: '0.75rem', fontWeight: 600 }}>
                  {collabDetail?.room?.name || 'Room'}
                </span>
              </div>

              <div style={{ flex: 1, overflowY: 'auto', padding: '6px 8px' }}>
                {/* Pinned URLs */}
                {(collabDetail?.room?.pins || collabDetail?.pins || []).slice(0, 5).map((p, i) => (
                  <a key={i} href={p.url} target="_blank" rel="noopener noreferrer"
                    style={{
                      display: 'block', padding: '4px 8px', borderRadius: 6, marginBottom: 3,
                      background: 'rgba(245, 158, 11,0.08)', color: '#93c5fd', fontSize: '0.7rem',
                      textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                    }}>
                    <i className="fas fa-thumbtack" style={{ marginRight: 4, color: '#60a5fa' }} />
                    {p.title || p.url}
                  </a>
                ))}
                {/* Messages */}
                {(collabDetail?.messages || []).slice(-20).map((m, i) => (
                  <div key={m.id || i} style={{ marginBottom: 4, padding: '6px 10px', borderRadius: 8, background: 'rgba(14, 165, 233, 0.08)', border: '1px solid rgba(14, 165, 233, 0.18)', display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                    <div
                      onClick={() => m.user_id && goToProfile(m.user_id)}
                      style={{ cursor: 'pointer', flexShrink: 0, marginTop: 1 }}
                    >
                      {m.avatar_url ? (
                        <img src={m.avatar_url} alt="" style={{ width: 20, height: 20, borderRadius: '50%', objectFit: 'cover', border: '1px solid rgba(14, 165, 233, 0.35)' }} />
                      ) : (
                        <div style={{ width: 20, height: 20, borderRadius: '50%', background: 'rgba(14, 165, 233, 0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.5rem', color: '#0369A1', fontWeight: 700 }}>
                          {(m.username || 'U')[0].toUpperCase()}
                        </div>
                      )}
                    </div>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <span
                        onClick={() => m.user_id && goToProfile(m.user_id)}
                        style={{ color: '#0369A1', fontSize: '0.65rem', fontWeight: 700, cursor: 'pointer' }}
                      >{m.username || 'User'}</span>
                      <p style={{ color: '#0F172A', fontSize: '0.78rem', margin: '2px 0 0', lineHeight: 1.4, wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>{m.content}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pin URL */}
              <div style={{ padding: '8px', borderTop: '1px solid rgba(14, 165, 233, 0.18)' }}>
                <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                  <input value={pinUrl} onChange={e => setPinUrl(e.target.value)}
                    placeholder="Pin a URL..."
                    data-testid="collab-pin-input"
                    style={{
                      flex: 1, background: '#ffffff', border: '1px solid rgba(14, 165, 233, 0.28)',
                      borderRadius: 8, padding: '6px 10px', color: '#0F172A', fontSize: '0.75rem', outline: 'none'
                    }}
                  />
                  <button onClick={pinUrlToCollab} data-testid="collab-pin-btn" style={{
                    background: 'linear-gradient(135deg, #0EA5E9, #0369A1)', border: 'none', borderRadius: 8, padding: '6px 12px',
                    color: '#ffffff', cursor: 'pointer', fontSize: '0.7rem', fontWeight: 700,
                    boxShadow: '0 2px 6px rgba(14, 165, 233, 0.3)'
                  }}>Pin</button>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <textarea value={collabMsg} onChange={e => setCollabMsg(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendCollabMsg(); } }}
                    placeholder="Message... (Shift+Enter for new line)"
                    data-testid="collab-message-input"
                    rows={2}
                    style={{
                      flex: 1, background: '#ffffff', border: '1px solid rgba(14, 165, 233, 0.28)',
                      borderRadius: 8, padding: '6px 10px', color: '#0F172A', fontSize: '0.75rem', outline: 'none',
                      resize: 'none', fontFamily: 'inherit', minHeight: 36
                    }}
                  />
                  <button onClick={sendCollabMsg} data-testid="collab-send-btn" style={{
                    background: 'linear-gradient(135deg, #10B981, #059669)', border: 'none', borderRadius: 8, padding: '6px 12px',
                    color: '#ffffff', cursor: 'pointer', fontSize: '0.7rem', fontWeight: 700,
                    boxShadow: '0 2px 6px rgba(16, 185, 129, 0.3)'
                  }}>Send</button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CompactChatCollabModule;
